import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SearchBar from "@/components/feature/SearchBar";
import ExpenseTable from "@/components/feature/ExpenseTable";
import ExpenseCard from "@/components/feature/ExpenseCard";
import EmptyState from "@/components/feature/EmptyState";
import Button from "@/components/base/Button";
import Select from "@/components/base/Select";
import { useToast } from "@/store/ToastContext";
import { PAYMENT_METHODS } from "@/utils/format";
import { expenseApi, categoryApi, settingsApi } from "@/services/api";
import type { Expense, Category, AppSettings } from "@/models/types";

type SortKey = "date-desc" | "date-asc" | "amount-desc" | "amount-asc" | "category";

export default function Expenses() {
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [payment, setPayment] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<SortKey>("date-desc");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [expensesData, categoriesData, settingsData] = await Promise.all([
          expenseApi.list(),
          categoryApi.list(),
          settingsApi.get(),
        ]);

        // Mapear si el backend usa snake_case para category_id y payment_method
        const mappedExpenses = expensesData.map((e: any) => ({
          ...e,
          categoryId: e.category_id ?? e.categoryId,
          paymentMethod: e.payment_method ?? e.paymentMethod,
          createdAt: e.created_at ?? e.createdAt,
        }));

        setExpenses(mappedExpenses);
        setCategories(categoriesData);
        setSettings(settingsData);
      } catch (error) {
        console.error("Error cargando gastos:", error);
        showToast("Error al cargar los gastos.", "error");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const currency = settings?.currency ?? "USD";

  const filtered = useMemo(() => {
    let result: Expense[] = expenses.filter((e) => {
      if (search && !e.description.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (category && e.categoryId !== category) return false;
      if (payment && e.paymentMethod !== payment) return false;
      if (minAmount && e.amount < Number(minAmount)) return false;
      if (maxAmount && e.amount > Number(maxAmount)) return false;
      if (dateFrom && e.date < dateFrom) return false;
      if (dateTo && e.date > dateTo) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      switch (sort) {
        case "date-asc":
          return a.date.localeCompare(b.date);
        case "amount-desc":
          return b.amount - a.amount;
        case "amount-asc":
          return a.amount - b.amount;
        case "category":
          return a.categoryId.localeCompare(b.categoryId);
        default:
          return b.date.localeCompare(a.date);
      }
    });
    return result;
  }, [expenses, search, category, payment, minAmount, maxAmount, dateFrom, dateTo, sort]);

  const hasActiveFilters =
    search || category || payment || minAmount || maxAmount || dateFrom || dateTo;

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setPayment("");
    setMinAmount("");
    setMaxAmount("");
    setDateFrom("");
    setDateTo("");
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-4 mb-5">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Buscar por descripción..."
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon="ri-filter-3-line"
            onClick={() => setShowFilters((v) => !v)}
          >
            Filtros {hasActiveFilters && "•"}
          </Button>
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="w-auto min-w-[160px]"
            options={[
              { value: "date-desc", label: "Más recientes" },
              { value: "date-asc", label: "Más antiguos" },
              { value: "amount-desc", label: "Mayor importe" },
              { value: "amount-asc", label: "Menor importe" },
              { value: "category", label: "Por categoría" },
            ]}
          />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="bg-background-100 border border-background-200 rounded-lg p-4 mb-5 animate-fade-in-up">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Select
              label="Categoría"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={[
                { value: "", label: "Todas las categorías" },
                ...categories.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            <Select
              label="Método de pago"
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
              options={[
                { value: "", label: "Todos los métodos" },
                ...PAYMENT_METHODS.map((p) => ({ value: p.value, label: p.label })),
              ]}
            />
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                Rango de importe
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Mín."
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <span className="text-foreground-500">—</span>
                <input
                  type="number"
                  placeholder="Máx."
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-foreground-500 mb-3">
        {filtered.length} gasto{filtered.length !== 1 ? "s" : ""}
      </p>

      {filtered.length === 0 ? (
        <div className="bg-background-50 border border-background-200 rounded-lg">
          <EmptyState
            icon="ri-wallet-3-line"
            title="Sin gastos registrados"
            description="Empieza a controlar tus gastos registrando tu primera compra."
            action={
              <Link
                to="/gastos/nuevo"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-line" /> Registrar mi primer gasto
              </Link>
            }
          />
        </div>
      ) : (
        <>
          <ExpenseTable expenses={filtered} currencyCode={currency} />
          <div className="md:hidden space-y-3">
            {filtered.map((e) => (
              <ExpenseCard key={e.id} expense={e} currencyCode={currency} />
            ))}
          </div>
        </>
      )}
    </AppLayout>
  );
}